import { ContactService } from "../services/contactService.js"
import { FishTagService } from "../services/fishTagService.js"

/**
 * Take-home fish tag label printing via the browser's native print dialog.
 *
 * Mirrors the demo (SportOMSBootv2 lodgeFishRoomPrint.ts): the label markup +
 * CSS are written into a hidden, same-origin iframe and that iframe is printed
 * directly. This keeps the 4×6 page size, avoids a PDF rendering layer, and
 * relies on system fonts (Helvetica/Arial + Courier New) for the look.
 */

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
}

/** Tags longer than 4 chars use a wide rounded pill (wraps up to 2 lines). */
function isLongTag(tag) {
    return String(tag ?? "").trim().length > 4
}

/** 0.8in take-home label badges — separate scale from 42px box-label v2 UI. */
const FTL_TAG_CIRCLE_FONT_SIZE_PX = 30
const FTL_TAG_PILL_FONT_SIZE_PX = 22

function buildTagHtml(tag) {
    const clean = String(tag ?? "").trim()
    if (!clean) {
        return ""
    }
    if (isLongTag(clean)) {
        return `<div class="ftl-tag-pill"><span class="ftl-tag-pill-text">${escapeHtml(
            clean
        )}</span></div>`
    }
    return `<div class="ftl-tag-circle" style="font-size:${FTL_TAG_CIRCLE_FONT_SIZE_PX}px;">${escapeHtml(
        clean
    )}</div>`
}

/** One circle/pill per comma-separated tag, laid out in a wrapping row. */
function buildTagGroupHtml(fishTag) {
    const tags = FishTagService.splitFishTags(fishTag)
    if (tags.length === 0) {
        return ""
    }
    return `<div class="ftl-tag-group">${tags.map(buildTagHtml).join("")}</div>`
}

/**
 * Inlined into the print iframe so no external stylesheet/asset is required.
 * `@page { margin: 0 }` collapses the printable margin so Chrome/Edge omit the
 * default URL/date headers and footers, and the page is locked to 4in × 6in.
 */
const FISH_TAG_LABEL_STYLES = `
  @page { size: 4in 6in; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Helvetica, Arial, sans-serif;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ftl-label {
    width: 4in;
    height: 6in;
    padding: 0.28in 0.3in 0.25in;
    display: flex;
    flex-direction: column;
    text-align: left;
    overflow: hidden;
  }
  /* Break *before* each subsequent label rather than *after* every label: a
     page break is only ever inserted between labels, never after the final one,
     which avoids the trailing blank page Chrome emits when a full-height block
     ends exactly on the page boundary. */
  .ftl-label + .ftl-label {
    page-break-before: always;
    break-before: page;
  }
  .ftl-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    /* row-gap matches .ftl-name margin-top so the gap above a wrapped trip
       line equals the gap between the trip line and the name below it */
    gap: 0.14in 0.15in;
    flex-shrink: 0;
  }
  .ftl-tag-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.1in;
    /* keep tags at full size and let trip drop below when there's no room
       beside them, but cap to the label width so many tags wrap internally */
    flex: 0 0 auto;
    max-width: 100%;
    min-width: 0;
  }
  .ftl-tag-group > * { flex-shrink: 0; }
  .ftl-tag-circle {
    width: 0.8in;
    height: 0.8in;
    border-radius: 50%;
    background: #000;
    color: #fff;
    font-weight: 900;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ftl-tag-pill {
    max-width: 2.4in;
    min-height: 0.58in;
    border-radius: 0.16in;
    background: #000;
    color: #fff;
    font-weight: 900;
    font-size: ${FTL_TAG_PILL_FONT_SIZE_PX}px;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1.05;
    padding: 0.08in 0.18in;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    overflow: hidden;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .ftl-tag-pill-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-all;
  }
  .ftl-trip {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-align: right;
    white-space: nowrap;
    /* grow to fill its line (keeps right-alignment), never shrink/wrap
       internally, so it drops to its own line when it can't fit beside the tag */
    flex: 1 0 auto;
  }
  .ftl-name {
    font-size: 32px;
    font-weight: 900;
    line-height: 1.1;
    letter-spacing: 0.02em;
    word-break: break-word;
    margin-top: 0.14in;
    flex-shrink: 0;
  }
  .ftl-license {
    font-size: 17px;
    font-weight: 700;
    font-family: 'Courier New', Courier, monospace;
    letter-spacing: 0.03em;
    margin-top: 0.1in;
    flex-shrink: 0;
  }
  .ftl-fish-list {
    flex: 1;
    margin-top: 0.18in;
    padding-top: 0.14in;
    border-top: 2px solid #000;
    min-height: 0;
    overflow: hidden;
  }
`

function buildLabelData(customer) {
    return {
        tag: String(customer?.fish_tag ?? "").trim(),
        tripCode: String(customer?.trip_code ?? "").trim(),
        customerName: ContactService.getPrimaryName(customer)
            .lastCommaFirst()
            .toUpperCase(),
        fishingLicense: String(customer?.fishing_license ?? "").trim(),
    }
}

function buildLabelPageHtml(label) {
    return `<section class="ftl-label">
  <header class="ftl-head">
    ${buildTagGroupHtml(label.tag)}
    <div class="ftl-trip">trip: ${escapeHtml(label.tripCode)}</div>
  </header>
  <div class="ftl-name">${escapeHtml(label.customerName)}</div>
  <div class="ftl-license">Fishing License: ${escapeHtml(label.fishingLicense)}</div>
  <div class="ftl-fish-list" aria-hidden="true"></div>
</section>`
}

/**
 * Writes the label HTML into a hidden iframe and prints just that iframe, so
 * the host app's DOM/styles are never part of the printout.
 */
function printHtmlDocument(html) {
    if (typeof document === "undefined") {
        return
    }

    const iframe = document.createElement("iframe")
    iframe.setAttribute("aria-hidden", "true")
    Object.assign(iframe.style, {
        position: "fixed",
        right: "0",
        bottom: "0",
        width: "0",
        height: "0",
        border: "none",
        opacity: "0",
        pointerEvents: "none",
    })
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) {
        if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe)
        }
        return
    }

    let cleaned = false
    const cleanup = () => {
        if (cleaned) return
        cleaned = true
        if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe)
        }
    }

    win.addEventListener("afterprint", cleanup)

    doc.open()
    doc.write(html)
    doc.close()

    const triggerPrint = () => {
        try {
            win.focus()
            win.print()
        } finally {
            // Fallback cleanup for browsers that never fire `afterprint`.
            setTimeout(cleanup, 2000)
        }
    }

    // Ensure styles/fonts are applied before printing so the first print is
    // never an unstyled/blank frame.
    const fontsReady =
        doc.fonts && typeof doc.fonts.ready?.then === "function"
            ? doc.fonts.ready
            : Promise.resolve()

    Promise.resolve(fontsReady)
        .catch(() => {})
        .finally(() => {
            setTimeout(triggerPrint, 50)
        })
}

/** Print a single 4×6 take-home fish tag label for the given customer. */
export function printFishTagLabel(customer) {
    if (!customer) {
        return
    }
    printFishTagLabels([customer])
}

/** Print one 4×6 take-home fish tag label per customer (in array order). */
export function printFishTagLabels(customers) {
    const labels = (customers ?? [])
        .filter(Boolean)
        .map(buildLabelData)
    if (labels.length === 0) {
        return
    }

    const body = labels.map(buildLabelPageHtml).join("")
    const title = labels.length > 1 ? "Take home fish tag labels" : "Take home fish tag label"
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title><style>${FISH_TAG_LABEL_STYLES}</style></head><body>${body}</body></html>`

    printHtmlDocument(html)
}
